import * as React from 'react';
import { useId } from 'react';
import { IconButton, Switch, Tooltip } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';

export interface SettingInfo {
  title: string;
  text: string;
}

interface Props {
  label: string;
  helper?: string;
  info?: SettingInfo;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}

/**
 * One line of the settings: a switch, its label and a grey helper beneath. Settings save on
 * change, so the control is always a switch -- a checkbox would promise a Save button.
 */
export const SettingRow = ({ label, helper, info, checked, disabled, onChange }: Props) => {
  const id = useId();
  const inputProps = {
    id: `${id}-control`,
    'aria-describedby': helper ? `${id}-helper` : undefined,
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    onChange(event.target.checked);

  return (
    <div className={'setting-row' + (disabled ? ' setting-row--disabled' : '')}>
      <div className={'setting-row__control'}>
        <Switch
          size={'small'}
          checked={checked}
          disabled={disabled}
          onChange={handleChange}
          slotProps={{ input: inputProps }}
        />
      </div>
      <div className={'setting-row__text'}>
        <div className={'setting-row__label-row'}>
          <label className={'setting-row__label'} htmlFor={inputProps.id}>
            {label}
          </label>
          {info && <SettingInfoButton info={info} />}
        </div>
        {helper && (
          <p className={'setting-row__helper'} id={`${id}-helper`}>
            {helper}
          </p>
        )}
      </div>
    </div>
  );
};

/** The ⓘ next to a label; hovering or tapping it opens the minihelp */
export const SettingInfoButton = ({ info }: { info: SettingInfo }) => (
  <Tooltip
    enterTouchDelay={0}
    classes={{ tooltip: 'setting-row__minihelp' }}
    title={
      <>
        <span className={'setting-row__minihelp-title'}>{info.title}</span>
        <span className={'setting-row__minihelp-text'}>{info.text}</span>
      </>
    }
  >
    <IconButton size={'small'} className={'setting-row__info'} aria-label={info.title}>
      <InfoOutlined />
    </IconButton>
  </Tooltip>
);

interface CardProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

export const SettingsCard = ({ title, subtitle, children }: CardProps) => (
  <section className={'settings-card'}>
    <h2 className={'settings-card__title'}>{title}</h2>
    {subtitle && <p className={'settings-card__subtitle'}>{subtitle}</p>}
    {children}
  </section>
);

/** Rows depending on the one above are indented by the list, not by the row itself */
export const SettingNested = ({ children }: { children: React.ReactNode }) => (
  <div className={'setting-nested'}>{children}</div>
);
